import * as Moment from 'moment';
import * as MomentRange from 'moment-range';
import { clone, countBy, find, map, max, merge, path, pathEq, propEq, propOr, reduce } from 'ramda';

const moment = MomentRange.extendMoment(Moment);

export interface IFilter {
  datefrom: Date;
  dateto: Date;
}

export interface IWidgetOptions {
  title: string;
  func: Function;
  filter?: IFilter;
  requiredParams?: Array<string>;
  postProcess?: Function;
  chartType?: 'bar' | 'line';
  pickerType?: 'calendar' | 'both';
}

export interface IPhase {
  name: string;
  title: string;
  tops: Array<IWidgetOptions>;
  histograms: Array<IWidgetOptions>;
}

export const phases = (
  statsService,
  translateService,
  sessionStartDate,
  sessionEndDate
): Array<IPhase> => {
  const lang = translateService.store.currentLang || translateService.store.defaultLang;
  const dateFormat = lang === 'french' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';

  const sessionStart = moment(sessionStartDate)
    .subtract(moment(sessionStartDate).minutes() % 5, 'minutes')
    .startOf('minutes');
  const sessionEnd = moment(sessionEndDate).endOf('minutes');

  const beforeLiveMonthFilter: IFilter = {
    datefrom: moment(sessionStart)
      .subtract(1, 'month')
      .startOf('day')
      .toDate(),
    dateto: moment(sessionStart)
      .endOf('day')
      .toDate()
  };

  const duringLiveFilter: IFilter = {
    datefrom: moment(sessionStart).toDate(),
    dateto: moment(sessionEnd).toDate()
  };

  const afterLiveFilter: IFilter = {
    datefrom: moment(sessionEnd)
      .startOf('day')
      .toDate(),
    dateto: moment(sessionEnd)
      .add(1, 'month')
      .endOf('day')
      .toDate()
  };

  const resolveValue: any = data => propOr(0, 'value')(data);

  const resolveInMinutes = data => {
    const inSeconds = resolveValue(data);
    return Math.round(inSeconds / 60);
  };

  const resolveTimestamp = data => {
    return data.map(en => {
      return {
        date: moment.unix(en.timestamp).format(dateFormat),
        value: en.value
      };
    });
  };

  const applyRange = (raw_data, options) => {
    const datefrom: Date = path(['params', 'datefrom'], options);
    const dateto: Date = path(['params', 'dateto'], options);
    const data = resolveTimestamp(raw_data);
    const range = moment().range(datefrom, dateto);
    const rangeArr = Array.from(range.by('days'));
    const rangeData = rangeArr.map(dt => ({
      date: dt.format(dateFormat),
      value: 0
    }));
    return rangeData.map(en => {
      const update = find(propEq('date', en.date))(data);
      return update ? update : en;
    });
  };

  const createRange = (datefrom, dateto, interval, step = 1) => {
    const range = moment.range(datefrom, dateto);
    const rangeArr = Array.from(range.by(interval, { step }));
    return map((r: any) => moment.range(r, moment(r).add(step, interval.replace(/s$/, ''))))(
      rangeArr
    );
  };

  const getUsers = data =>
    map((en: any) => moment.range(moment(en.jointime), moment(en.lasttime)))(data);

  const sessionparam1NewConnections = (data, options) => {
    const datefrom: Date = path(['params', 'datefrom'], options);
    const dateto: Date = path(['params', 'dateto'], options);
    const users: any = getUsers(data);
    const ranges = createRange(datefrom, dateto, 'minutes', 5);
    return map((range: any) => {
      const count = countBy((user: any) => {
        return String(range.contains(user.start));
      })(users);
      return {
        date: moment(range.start).format('HH:mm'),
        value: count['true'] || 0
      };
    })(ranges);
  };

  const sessionparam1 = (data, options) => {
    const datefrom: Date = path(['params', 'datefrom'], options);
    const dateto: Date = path(['params', 'dateto'], options);
    const users: any = getUsers(data);
    const ranges = createRange(datefrom, dateto, 'minutes', 5);
    return map((range: any) => {
      const count = countBy((user: any) => {
        return String(range.overlaps(user));
      })(users);
      return {
        date: moment(range.start).format('HH:mm'),
        value: count['true'] || 0
      };
    })(ranges);
  };

  const sessionparam1Max = (data, options) => {
    const op = merge(options, { params: clone(duringLiveFilter) });
    const values = sessionparam1(data, op);
    return reduce(max, 0, map((v: any) => v.value)(values));
  };

  const getQuestions = data =>
    map((en: any) => ({ range: moment.range(moment(en.from), moment(en.to)), value: en.value }))(
      data
    );

  const questionsLive5minsCountList = (data, options) => {
    const datefrom: Date = path(['params', 'datefrom'], options);
    const dateto: Date = path(['params', 'dateto'], options);
    const questions: any = getQuestions(data);
    const ranges = createRange(datefrom, dateto, 'minutes', 5);
    return map((range: any) => {
      const update = find((en: any) => {
        return range.contains(en.range.start);
      })(questions);

      return {
        date: moment(range.start).format('HH:mm'),
        value: update ? update.value : 0
      };
    })(ranges);
  };

  return [
    {
      name: 'before',
      title: translateService.instant('sessions.stats.select.before_live'),
      tops: [
        {
          title: translateService.instant('sessions.stats.before_live.tops.number_of_questions'),
          func: statsService.questionsBeforeCount,
          postProcess: resolveValue
        },
        {
          title: translateService.instant('sessions.stats.before_live.tops.number_of_alert'),
          func: statsService.alertsCount,
          postProcess: resolveValue
        },
        {
          title: translateService.instant('sessions.stats.before_live.tops.total_number_of_visits'),
          func: statsService.beforeliveHitsCount,
          postProcess: resolveValue
        },
        {
          title: translateService.instant(
            'sessions.stats.before_live.tops.total_number_of_unique_visits'
          ),
          func: statsService.beforeliveUniqueCount,
          postProcess: resolveValue
        }
      ],
      histograms: [
        {
          title: translateService.instant(
            'sessions.stats.before_live.histograms.number_of_before_live_questions'
          ),
          func: statsService.questionsBeforeDayList,
          filter: clone(beforeLiveMonthFilter),
          postProcess: applyRange,
          requiredParams: ['datefrom', 'dateto']
        },
        {
          title: translateService.instant(
            'sessions.stats.before_live.histograms.number_of_unique_connections'
          ),
          func: statsService.beforeliveDaysUniqueList,
          filter: clone(beforeLiveMonthFilter),
          postProcess: resolveTimestamp,
          requiredParams: ['datefrom', 'dateto']
        },
        {
          title: translateService.instant(
            'sessions.stats.before_live.histograms.number_of_commulative_connections'
          ),
          func: statsService.beforeliveDaysHitsList,
          filter: clone(beforeLiveMonthFilter),
          postProcess: resolveTimestamp,
          requiredParams: ['datefrom', 'dateto']
        }
      ]
    },
    {
      name: 'during',
      title: translateService.instant('sessions.stats.select.during_live'),
      tops: [
        {
          title: translateService.instant('sessions.stats.during_live.tops.unique_number_of_users'),
          func: statsService.liveUsersUniqueCount,
          postProcess: resolveValue
        },
        {
          title: translateService.instant(
            'sessions.stats.during_live.tops.commulative_number_of_users'
          ),
          func: statsService.liveUsersHitsCount,
          postProcess: resolveValue
        },
        {
          title: translateService.instant(
            'sessions.stats.during_live.tops.number_of_questions_sent'
          ),
          func: statsService.questionsLiveCount,
          postProcess: resolveValue
        },
        {
          title: translateService.instant(
            'sessions.stats.during_live.tops.number_of_validated_questions'
          ),
          func: statsService.questionsPublishedCount,
          postProcess: resolveValue
        },
        {
          title: translateService.instant(
            'sessions.stats.during_live.tops.maximum_number_of_connections'
          ),
          func: statsService.sessionparam1List,
          postProcess: sessionparam1Max
        },
        {
          title: translateService.instant(
            'sessions.stats.during_live.tops.average_connection_time'
          ),
          func: statsService.liveUsersDurationAvg,
          postProcess: resolveInMinutes
        }
      ],
      histograms: [
        {
          title: translateService.instant(
            'sessions.stats.during_live.histograms.number_of_unique_users'
          ),
          func: statsService.sessionparam1List,
          postProcess: sessionparam1NewConnections,
          filter: clone(duringLiveFilter),
          pickerType: 'both',
          requiredParams: ['datefrom', 'dateto'],
          chartType: 'line'
        },
        {
          title: translateService.instant(
            'sessions.stats.during_live.histograms.number_of_questions_sent'
          ),
          func: statsService.questionsLive5minsCountList,
          filter: clone(duringLiveFilter),
          postProcess: questionsLive5minsCountList,
          pickerType: 'both',
          requiredParams: ['datefrom', 'dateto']
        },
        {
          title: translateService.instant(
            'sessions.stats.during_live.histograms.maximum_number_of_users'
          ),
          func: statsService.sessionparam1List,
          filter: clone(duringLiveFilter),
          postProcess: sessionparam1,
          pickerType: 'both',
          requiredParams: ['datefrom', 'dateto'],
          chartType: 'line'
        }
      ]
    },

    {
      name: 'after',
      title: translateService.instant('sessions.stats.select.after_live'),
      tops: [
        {
          title: translateService.instant('sessions.stats.after_live.tops.total_number_of_visits'),
          func: statsService.transcriptTodayHitsCount,
          postProcess: resolveValue
        },
        {
          title: translateService.instant('sessions.stats.after_live.tops.number_of_unique_visits'),
          func: statsService.transcriptUniqueCount,
          postProcess: resolveValue
        },
        {
          title: translateService.instant('sessions.stats.after_live.tops.number_of_downloads'),
          func: statsService.downloadCount,
          postProcess: resolveValue,
          requiredParams: ['fileid']
        }
      ],
      histograms: [
        {
          title: translateService.instant(
            'sessions.stats.after_live.histograms.number_of_unique_visits'
          ),
          func: statsService.transcriptDaysUniqueList,
          filter: clone(afterLiveFilter),
          postProcess: resolveTimestamp,
          requiredParams: ['datefrom', 'dateto']
        },
        {
          title: translateService.instant(
            'sessions.stats.after_live.histograms.number_of_comulative_visits'
          ),
          func: statsService.transcriptDaysHitsList,
          filter: clone(afterLiveFilter),
          postProcess: resolveTimestamp,
          requiredParams: ['datefrom', 'dateto']
        }
      ]
    }
  ];
};



// WEBPACK FOOTER //
// ./src/app/modules/sessions/stats/stats.ts